import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, Target, X } from 'lucide-react';
import { useAskSwirlStore } from '../../stores/askSwirlStore';
import FocusSelectorModal from './FocusSelectorModal';
import chatService from '../../services/chatService';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import AttachmentMenu from './AttachmentMenu';
import CardSelectorButton from './CardSelectorButton';
import CardStack from './CardStack';
import MessageAttachmentsStack from './MessageAttachmentsStack';

/**
 * Enhanced Chat Input Bar
 * Bottom unified input bar with chips, attachment menu, and send button
 */
export default function ChatInputBar({ onSend, isProcessing = false, messages = [], showDocsSidebar = false }) {
  // CRITICAL: Subscribe to chips directly to ensure re-renders
  const chips = useAskSwirlStore((state) => state.chips);
  const clearChips = useAskSwirlStore((state) => state.clearChips);
  const prefilledInput = useAskSwirlStore((state) => state.prefilledInput);
  const clearPrefilledInput = useAskSwirlStore((state) => state.clearPrefilledInput);
  const { isExpanded } = useSidebar();
  const [input, setInput] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const textareaRef = useRef(null);

  // Handle prefilled input from smart cards
  useEffect(() => {
    if (prefilledInput) {
      setInput(prefilledInput);
      clearPrefilledInput();
      // Focus the input after a brief delay to ensure it's rendered
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [prefilledInput, clearPrefilledInput]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  const handleSend = async () => {
    const trimmedInput = input.trim();
    if (!trimmedInput && chips.length === 0) {
      return;
    }
    if (!onSend) {
      return;
    }

    // Capture full chip data before clearing (needed for attachments)
    const chipsToSend = [...chips];

    // CRITICAL: Clear chips IMMEDIATELY before sending
    // This ensures CardStack disappears right away
    console.log('🧹 [ChatInputBar] Clearing chips before send. Current chips count:', chips.length);
    clearChips();
    
    // Force a microtask to ensure state propagates to all subscribers
    await new Promise(resolve => setTimeout(resolve, 0));
    
    // Read directly from store to verify
    const storeState = useAskSwirlStore.getState();
    console.log('✅ [ChatInputBar] Chips after clear (from store):', storeState.chips.length);

    // Clear input
    setInput('');
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Send message with context (pass full chip data for attachment building)
    onSend({
      prompt: trimmedInput,
      context: chipsToSend.map(c => ({
        id: c.id,
        type: c.type,
      })),
      // Pass full chip data separately so attachments can be built after chips are cleared
      chipData: chipsToSend,
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div 
      className="fixed bottom-0 z-50 pointer-events-none"
      style={{
        left: isExpanded ? '288px' : '64px', // Account for sidebar: ml-72 = 288px, ml-16 = 64px
        right: showDocsSidebar ? '420px' : 0, // Account for docs sidebar
        transition: 'left 800ms ease-out, right 800ms ease-out', // Explicit transitions only for position
      }}
    >
      <div className="flex items-center justify-center pb-6 px-4">
        <div className="relative pointer-events-auto w-full max-w-3xl">
          {/* Card Stack - positioned absolutely to the left of the container */}
          {/* Show chips from current session - ONLY if chips exist */}
          {/* CRITICAL: Use chips.length as key to force unmount/remount when chips change */}
          {chips.length > 0 ? (
            <div 
              key={`cardstack-${chips.length}-${chips.map(c => c.id).join('-')}`}
              className="absolute pointer-events-auto"
              style={{
                right: '100%',
                bottom: 0,
                marginRight: '110px',
              }}
            >
              <CardStack isProcessing={isProcessing} />
            </div>
          ) : null}
          
          {/* REMOVED: MessageAttachmentsStack should NOT show in CardStack area
              Attachments from loaded messages should ONLY appear in the chat messages themselves (3x3 grid)
              They should NOT appear in the CardStack area next to the input bar */}

          {/* Input Bar - centered in main content area */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className={`flex items-center gap-2 px-3 py-2.5 rounded-2xl transition-all duration-300 border ${
              isFocused
                ? 'bg-white/95 shadow-2xl shadow-purple-500/20 border-purple-200/60'
                : 'bg-white/90 shadow-xl hover:shadow-2xl border-neutral-200/60'
            }`}
            style={{
              backdropFilter: 'blur(40px)',
              WebkitBackdropFilter: 'blur(40px)',
            }}
          >
            {/* Card Selector Button */}
            <CardSelectorButton />

            {/* Attachment Menu (+ button) - only for uploads */}
            <AttachmentMenu />

            {/* Input Field */}
            <div className="flex-1 min-w-0">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                placeholder={chips.length > 0 ? "Type your message..." : "Ask Swirl anything..."}
                rows={1}
                className="w-full bg-transparent border-none outline-none text-neutral-800 placeholder:text-neutral-400 resize-none overflow-hidden text-[15px] leading-relaxed"
                style={{ maxHeight: '120px' }}
              />
            </div>

            {/* Send Button */}
            <button
              onClick={handleSend}
              disabled={!input.trim() && chips.length === 0}
              className="flex-shrink-0 p-2 rounded-full transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:bg-purple-100/50 active:scale-95"
            >
              <Send 
                size={20} 
                className={`transition-colors ${
                  input.trim() || chips.length > 0
                    ? 'text-purple-600'
                    : 'text-neutral-400'
                }`}
              />
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

